// Lightweight SVG analyzer.
//
// Runs in both Worker (Hono's body-as-text) and the browser. We avoid xmldom /
// jsdom — both pull megabytes of code we don't need. Instead we use a tiny
// regex-driven scan that's accurate enough for the geometry hints we want.

export interface PathInfo {
  id: string;
  d: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  bbox?: { x: number; y: number; w: number; h: number };
}

export interface LogoAnalysis {
  viewBox: { x: number; y: number; w: number; h: number };
  paths: PathInfo[];
  pathCount: number;
  groups: number;
  palette: string[];
  aspect: number;
  shape: "circular" | "horizontal" | "vertical" | "square";
  hasText: boolean;
  hasOpenPaths: boolean;
  totalLengthEstimate: number;
}

const ATTR = (name: string) =>
  new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, "i");

function parseViewBox(svgOpenTag: string): LogoAnalysis["viewBox"] {
  const m = svgOpenTag.match(ATTR("viewBox"));
  const v = (m?.[1] ?? m?.[2] ?? "").trim();
  if (v) {
    const [x, y, w, h] = v.split(/[\s,]+/).map(Number);
    if ([x, y, w, h].every(Number.isFinite)) return { x, y, w, h };
  }
  // Fallback: try width / height
  const wm = svgOpenTag.match(ATTR("width"));
  const hm = svgOpenTag.match(ATTR("height"));
  const w = parseFloat(wm?.[1] ?? wm?.[2] ?? "1024");
  const h = parseFloat(hm?.[1] ?? hm?.[2] ?? "1024");
  return { x: 0, y: 0, w: Number.isFinite(w) ? w : 1024, h: Number.isFinite(h) ? h : 1024 };
}

function findPaths(svg: string): PathInfo[] {
  const out: PathInfo[] = [];
  // Match <path …/> (self-closed) or <path …></path>.
  const re = /<path\b([^>]*?)\/?>(?:<\/path>)?/gi;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(svg))) {
    const attrs = m[1] ?? "";
    const d = (attrs.match(ATTR("d")) || [])[1] ?? (attrs.match(ATTR("d")) || [])[2];
    if (!d) continue;
    const idM = attrs.match(ATTR("id"));
    out.push({
      id: (idM?.[1] ?? idM?.[2]) || `p${i}`,
      d,
      fill: (attrs.match(ATTR("fill")) || [])[1] ?? (attrs.match(ATTR("fill")) || [])[2],
      stroke: (attrs.match(ATTR("stroke")) || [])[1] ?? (attrs.match(ATTR("stroke")) || [])[2],
      strokeWidth: parseFloat(
        (attrs.match(ATTR("stroke-width")) || [])[1] ?? (attrs.match(ATTR("stroke-width")) || [])[2] ?? "0"
      ) || undefined,
      bbox: estimateBBox(d),
    });
    i++;
  }
  return out;
}

// Cheap bbox: lift all numeric tokens out of `d` and treat them as alternating
// (x, y) pairs. Slight over-estimate, but the LLM only uses it as a hint.
function estimateBBox(d: string): { x: number; y: number; w: number; h: number } {
  const nums = d.match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi);
  if (!nums || nums.length < 2) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = parseFloat(nums[i]); const y = parseFloat(nums[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  }
  if (minX === Infinity) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function pickColors(svg: string): string[] {
  const set = new Set<string>();
  for (const m of svg.matchAll(/(?:fill|stroke)\s*=\s*"([^"]+)"/gi)) {
    const v = m[1];
    if (v && v !== "none" && v !== "currentColor" && v !== "transparent") set.add(v.toLowerCase());
  }
  for (const m of svg.matchAll(/(?:fill|stroke)\s*:\s*([^;"']+)/gi)) {
    const v = m[1].trim();
    if (v && v !== "none" && v !== "currentColor" && v !== "transparent") set.add(v.toLowerCase());
  }
  return [...set].slice(0, 16);
}

function classifyShape(aspect: number): LogoAnalysis["shape"] {
  if (aspect >= 0.85 && aspect <= 1.15) return "circular"; // ~square => often a mark
  if (aspect > 1.15 && aspect < 2.5) return "square";
  if (aspect >= 2.5) return "horizontal";
  return "vertical";
}

export function analyzeSvg(svgText: string): LogoAnalysis {
  const openTagMatch = svgText.match(/<svg\b[^>]*>/i);
  const openTag = openTagMatch?.[0] ?? "<svg>";
  const viewBox = parseViewBox(openTag);
  const paths = findPaths(svgText);
  const groups = (svgText.match(/<g\b/gi) || []).length;
  const palette = pickColors(svgText);
  const aspect = viewBox.w > 0 ? viewBox.w / Math.max(viewBox.h, 1) : 1;
  const hasText = /<text\b/i.test(svgText);
  const hasOpenPaths = paths.some((p) => /[mzZ]/.test(p.d) === false || /[Z]/i.test(p.d) === false);
  const totalLengthEstimate = paths.reduce(
    (acc, p) => acc + (p.bbox ? 2 * (p.bbox.w + p.bbox.h) : 0),
    0
  );
  return {
    viewBox,
    paths,
    pathCount: paths.length,
    groups,
    palette,
    aspect,
    shape: classifyShape(aspect),
    hasText,
    hasOpenPaths,
    totalLengthEstimate,
  };
}

// Re-emits the SVG with a `data-id` per path, so timelines can target them
// without depending on author-provided `id`s.
export function tagSvgPaths(svgText: string): string {
  let i = 0;
  return svgText.replace(/<path\b/gi, () => `<path data-id="p${i++}"`);
}
